import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { siteJsonLd } from '../utils/seo';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { useShuls } from '../hooks/useShuls';
import { useZmanim } from '../hooks/useZmanim';
import { getTodayString, getTomorrowString } from '../utils/dateShortcuts';
import ZmanimToday from '../components/public/ZmanimToday';
import MinyanimToday from '../components/public/MinyanimToday';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('seo.home'), {
    description: t('seo.desc.home'),
    jsonLd: siteJsonLd(i18n.language === 'en' ? 'en' : 'he'),
  });
  const Chevron = i18n.language === 'he' ? ChevronLeft : ChevronRight;
  const { shuls, loading: shulsLoading } = useShuls();
  const { zmanim: todayZmanim, loading: todayLoading } = useZmanim(getTodayString());
  const { zmanim: tomorrowZmanim, loading: tomorrowLoading } = useZmanim(getTomorrowString());
  const loading = shulsLoading || todayLoading || tomorrowLoading;
  const [searchParams] = useSearchParams();

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* The page's main heading, for screen readers (the design shows the sections instead). */}
      <h1 className="sr-only">{t('seo.home')}</h1>
      {searchParams.get('accountDeleted') && (
        <p role="status" className="text-sm rounded-[10px] bg-fog text-ink p-3">
          {t('privacy.deleteAccount.done')}
        </p>
      )}
      <ZmanimToday todayZmanim={todayZmanim} tomorrowZmanim={tomorrowZmanim} loading={loading} />
      <MinyanimToday shuls={shuls} todayZmanim={todayZmanim} tomorrowZmanim={tomorrowZmanim} loading={loading} />
      {/* Small, secondary entry point: the home page stays minyan-first (plan 8.5). */}
      <Link
        to="/directory"
        className="flex items-center gap-3 rounded-[16px] border border-ink/10 px-4 min-h-14 py-3 text-ink hover:bg-fog transition-colors"
      >
        <LayoutGrid className="w-5 h-5 shrink-0 text-ash" aria-hidden />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold">{t('directory.homeLink')}</span>
          <span className="block text-xs text-ash truncate">{t('directory.homeLinkHint')}</span>
        </span>
        <Chevron className="w-4 h-4 shrink-0 text-ash" aria-hidden />
      </Link>
    </div>
  );
}
